import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { WIDGETS_RECAPTCHA_CONFIG } from '../../tokens/widgets-recaptcha-config.token';

const RECAPTCHA_EXPLICIT_SCRIPT_SRC =
  'https://www.google.com/recaptcha/api.js?render=explicit';

const RECAPTCHA_READY_TIMEOUT_MS = 15_000;

/** Mirrors app RecaptchaService; keys from WIDGETS_RECAPTCHA_CONFIG. */
@Injectable({ providedIn: 'root' })
export class WidgetRecaptchaService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly config = inject(WIDGETS_RECAPTCHA_CONFIG, { optional: true });
  private v3ScriptPromise: Promise<void> | null = null;
  private v2ExplicitScriptPromise: Promise<void> | null = null;
  private globalCallbackSeq = 0;

  isConfigured(): boolean {
    return !!this.config?.v3SiteKey?.trim();
  }

  async executeV3(action: string): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('reCAPTCHA is only available in the browser');
    }

    const siteKey = this.config?.v3SiteKey?.trim();
    if (!siteKey) {
      throw new Error('reCAPTCHA v3 site key is not configured');
    }

    await this.loadV3Script(siteKey);

    return new Promise<string>((resolve, reject) => {
      window.grecaptcha?.ready(() => {
        window
          .grecaptcha!.execute(siteKey, { action })
          .then(resolve)
          .catch(reject);
      });
    });
  }

  async renderV2(
    container: HTMLElement,
    onResolved: (token: string) => void,
    onExpired: () => void,
    onError: () => void,
  ): Promise<number> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('reCAPTCHA is only available in the browser');
    }

    const siteKey = this.config?.v2SiteKey?.trim();
    if (!siteKey) {
      throw new Error('reCAPTCHA v2 site key is not configured');
    }

    container.replaceChildren();
    await this.ensureV2OnlyApi();

    return new Promise<number>((resolve, reject) => {
      window.grecaptcha?.ready(() => {
        try {
          const resolvedCallback = this.registerGlobalTokenCallback(onResolved);
          const expiredCallback = this.registerGlobalVoidCallback(onExpired);
          const errorCallback = this.registerGlobalVoidCallback(onError);

          const widgetId = window.grecaptcha!.render(container, {
            sitekey: siteKey,
            callback: resolvedCallback,
            'expired-callback': expiredCallback,
            'error-callback': errorCallback,
          });
          resolve(widgetId);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  getV2Response(widgetId: number): string {
    if (!isPlatformBrowser(this.platformId) || !window.grecaptcha?.getResponse) {
      return '';
    }
    return window.grecaptcha.getResponse(widgetId) ?? '';
  }

  resetV2(widgetId?: number): void {
    if (!isPlatformBrowser(this.platformId) || !window.grecaptcha) {
      return;
    }
    window.grecaptcha.reset(widgetId);
  }

  /**
   * v3 (`api.js?render=<siteKey>`) and v2 explicit cannot safely coexist; mixed loads
   * cause cross-origin SecurityError inside Google's bframe.
   */
  private async ensureV2OnlyApi(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const hasV3Script = this.hasV3ScriptLoaded();
    const hasExplicit = this.hasScriptWithSrc(RECAPTCHA_EXPLICIT_SCRIPT_SRC);

    if (!hasV3Script && hasExplicit && window.grecaptcha?.render) {
      await this.waitForGrecaptchaReady();
      return;
    }

    this.teardownRecaptchaScripts();
    this.v3ScriptPromise = null;
    this.v2ExplicitScriptPromise = this.injectExplicitV2Script();
    await this.v2ExplicitScriptPromise;
    await this.waitForGrecaptchaReady();

    if (!window.grecaptcha?.render) {
      throw new Error('reCAPTCHA v2 render API is unavailable');
    }
  }

  private injectExplicitV2Script(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const onloadName = `__ufpRecaptchaExplicitOnload_${Date.now()}`;
      const win = window as unknown as Record<string, () => void>;
      win[onloadName] = () => {
        delete win[onloadName];
        resolve();
      };

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?onload=${onloadName}&render=explicit`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        delete win[onloadName];
        reject(new Error('Failed to load reCAPTCHA v2 script'));
      };
      document.head.appendChild(script);
    });
  }

  private teardownRecaptchaScripts(): void {
    document
      .querySelectorAll('script[src*="recaptcha/api.js"]')
      .forEach((node) => node.remove());

    document
      .querySelectorAll('.grecaptcha-badge')
      .forEach((node) => node.remove());

    if (window.grecaptcha) {
      delete window.grecaptcha;
    }

    this.v2ExplicitScriptPromise = null;
  }

  private registerGlobalTokenCallback(handler: (token: string) => void): string {
    const name = `ufpRecaptchaCb_${++this.globalCallbackSeq}_${Date.now()}`;
    (window as unknown as Record<string, (token: string) => void>)[name] = (
      token: string,
    ) => {
      try {
        handler(token);
      } finally {
        delete (window as unknown as Record<string, unknown>)[name];
      }
    };
    return name;
  }

  private registerGlobalVoidCallback(handler: () => void): string {
    const name = `ufpRecaptchaCb_${++this.globalCallbackSeq}_${Date.now()}`;
    (window as unknown as Record<string, () => void>)[name] = () => {
      try {
        handler();
      } finally {
        delete (window as unknown as Record<string, unknown>)[name];
      }
    };
    return name;
  }

  private loadV3Script(siteKey: string): Promise<void> {
    const src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    if (!this.v3ScriptPromise) {
      this.v3ScriptPromise = this.injectScript(src).then(() =>
        this.waitForGrecaptchaReady(),
      );
    }
    return this.v3ScriptPromise;
  }

  private hasV3ScriptLoaded(): boolean {
    const scripts = document.querySelectorAll<HTMLScriptElement>(
      'script[src*="recaptcha/api.js"]',
    );
    for (const script of scripts) {
      const src = script.src || script.getAttribute('src') || '';
      if (!src.includes('render=explicit')) {
        return true;
      }
    }
    return false;
  }

  private waitForGrecaptchaReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      const attachReady = () => {
        window.grecaptcha?.ready(() => resolve());
      };

      if (window.grecaptcha) {
        attachReady();
        return;
      }

      const started = Date.now();
      const timer = window.setInterval(() => {
        if (window.grecaptcha) {
          window.clearInterval(timer);
          attachReady();
          return;
        }
        if (Date.now() - started > RECAPTCHA_READY_TIMEOUT_MS) {
          window.clearInterval(timer);
          reject(new Error('reCAPTCHA API failed to initialize'));
        }
      }, 50);
    });
  }

  private injectScript(src: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    if (this.hasScriptWithSrc(src)) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  private hasScriptWithSrc(src: string): boolean {
    const scripts = document.querySelectorAll<HTMLScriptElement>(
      'script[src*="recaptcha/api.js"]',
    );
    for (const script of scripts) {
      if (script.src === src || script.getAttribute('src') === src) {
        return true;
      }
    }
    return false;
  }
}
